// Orphan detection for the admin screen: values in claims_pnl_monthly_values
// whose category string is not in that organisation's configured category
// list (app_settings.pnl_expense_categories).
//
// Run: deno test --allow-read src/features/pnlAnalytics/orphanCategoryValues.test.ts
// Run: npm run verify:pnl-orphan-category-values
//
// WHY THIS IS NOT THE UNMAPPED-CATEGORY LIST THAT ALREADY EXISTS.
// PnlAdminUnmapped already shows meta.unmappedCategories from
// pnl_analytics_query, but that list answers a different question: which
// category strings fail to RESOLVE TO A METRIC KEY, i.e. fall through
// pnl_metric_key_for to expense.unmapped (20260910000300, line 570). A
// category removed from the editor usually still has an alias row, because
// 20260908000200 backfilled one for every known string and the rename path
// writes more, so it resolves perfectly well and never appears on that list
// while being completely absent from the configuration. The two checks
// overlap on the Aire rows of 8 September 2026 and nowhere else: those two
// strings were never aliased, so they happen to fail both tests.
//
// This check asks the question the defect actually turned on. Data exists;
// is there anything configured that would ever show it?
//
// WHY source MATTERS AND IS REPORTED. Only source = 'manual' rows reach the
// V1 engine: both the project P&L tab and the company dashboard filter to
// manual, so a Xero-sourced orphan cannot move a total. Production carries
// 57 such rows today under the string "Income" (the Xero catch-all, mapped
// to revenue.xero_unmapped), and burying two real manual orphans under 57
// harmless Xero ones would defeat the point of the screen. They are ranked,
// not hidden: a Xero orphan still means a sync is writing a string nobody
// configured, which someone should eventually decide about.
//
// No React and no supabase here: the caller fetches, this module shapes.

import { formatAmount, formatMonthLabel } from "./categoryRemoval.ts";

export interface OrphanCategoryValueRow {
  category: string;
  /** claims_pnl_monthly_values.source. */
  source: string;
  /** claims_projects.job_location. */
  projectName: string;
  /** "YYYY-MM-01". */
  monthDate: string;
  amount: number;
}

export interface OrphanCategorySummary {
  category: string;
  rowCount: number;
  total: number;
  /** Formatted once here so the component has no formatting logic of its own. */
  totalLabel: string;
  /** Distinct sources behind these rows, sorted, e.g. ["manual"] or ["manual", "xero"]. */
  sources: string[];
  /**
   * True when at least one row is source = 'manual', which is the only source
   * the V1 P&L engine reads. These are the rows that could have changed a
   * profit figure, so they sort first and the screen says so.
   */
  reachesPnlEngine: boolean;
  /** "Aire - Newstead, Aug 2026" per row, in the order given, for display. */
  occurrences: string[];
}

/** Shown on the admin screen as one line each, e.g. "Aire - Newstead, Aug 2026". */
function describeOccurrence(row: OrphanCategoryValueRow): string {
  return `${row.projectName}, ${formatMonthLabel(row.monthDate)}`;
}

/**
 * Groups orphan rows by category string.
 *
 * Ordering is deliberate and is the whole value of the screen: categories
 * whose rows reach the P&L engine come first, then by row count descending,
 * then by name, so the thing that could be moving money is the first thing
 * an admin reads and the ordering is total rather than dependent on however
 * the database happened to return the rows.
 */
export function summariseOrphanCategoryValues(
  rows: OrphanCategoryValueRow[],
): OrphanCategorySummary[] {
  const byCategory = new Map<string, OrphanCategoryValueRow[]>();
  for (const row of rows) {
    const existing = byCategory.get(row.category);
    if (existing) {
      existing.push(row);
    } else {
      byCategory.set(row.category, [row]);
    }
  }

  const summaries: OrphanCategorySummary[] = [];
  for (const [category, categoryRows] of byCategory) {
    const total = categoryRows.reduce((sum, row) => sum + row.amount, 0);
    const sources = [...new Set(categoryRows.map((row) => row.source))].sort();
    summaries.push({
      category,
      rowCount: categoryRows.length,
      total,
      totalLabel: formatAmount(total),
      sources,
      reachesPnlEngine: sources.includes("manual"),
      occurrences: categoryRows.map(describeOccurrence),
    });
  }

  return summaries.sort((a, b) => {
    if (a.reachesPnlEngine !== b.reachesPnlEngine) return a.reachesPnlEngine ? -1 : 1;
    if (a.rowCount !== b.rowCount) return b.rowCount - a.rowCount;
    return a.category.localeCompare(b.category);
  });
}

/**
 * The one-line summary next to the section heading, so the collapsed state
 * still tells an admin whether anything needs them.
 */
export function describeOrphanTotals(summaries: OrphanCategorySummary[]): string {
  if (summaries.length === 0) return "none";
  const reaching = summaries.filter((s) => s.reachesPnlEngine);
  const rows = summaries.reduce((sum, s) => sum + s.rowCount, 0);
  const categoryWord = summaries.length === 1 ? "category" : "categories";
  const rowWord = rows === 1 ? "value" : "values";
  if (reaching.length === 0) {
    return `${summaries.length} ${categoryWord}, ${rows} ${rowWord}, none reaching the P&L engine`;
  }
  return `${summaries.length} ${categoryWord}, ${rows} ${rowWord}, ${reaching.length} reaching the P&L engine`;
}
